import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import { UPLOAD_URL } from '../../../config';
import './ModalEvidencias.css';

const ModalEvidencias = ({ show, onClose, actividad }) => {
  const { success, error, warning, confirm } = useNotification();
  const [evidencias, setEvidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [formData, setFormData] = useState({
    archivo: null,
    descripcion: '',
    fecha_captura: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (show && actividad) {
      cargarEvidencias();
    }
  }, [show, actividad]);

  const cargarEvidencias = async () => {
    try {
      setLoading(true);
      const gqlR = await client.query(`query($aid: Int!) { evidenciasActividad(actividad_id: $aid) { id tipo nombre_archivo nombre_original ruta_archivo descripcion fecha_captura } }`, { aid: actividad.id });
      setEvidencias(gqlR.evidenciasActividad || []);
    } catch (error) {
      console.error('Error al cargar evidencias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, archivo: file }));

      // Crear vista previa
      const reader = new FileReader();
      reader.onloadend = () => {
        setVistaPrevia(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubir = async (e) => {
    e.preventDefault();

    if (!formData.archivo) {
      warning('Por favor selecciona un archivo');
      return;
    }

    setSubiendo(true);

    try {
      const data = new FormData();
      data.append('actividad_id', actividad.id);
      data.append('archivo', formData.archivo);
      data.append('descripcion', formData.descripcion);
      data.append('fecha_captura', formData.fecha_captura);

      const { UPLOAD_URL } = await import('../../../config');
      const token = localStorage.getItem('tuvi_token');
      const response = await fetch(`${UPLOAD_URL}/evidencia`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: data
      });

      const result = await response.json();

      if (result.success) {
        success('Evidencia subida exitosamente');
        setFormData({
          archivo: null,
          descripcion: '',
          fecha_captura: new Date().toISOString().split('T')[0]
        });
        setVistaPrevia(null);
        document.getElementById('file-input').value = '';
        cargarEvidencias();
      } else {
        error('Error: ' + result.message);
      }
    } catch (err) {
      console.error('Error al subir evidencia:', err);
      error('Error al subir la evidencia');
    } finally {
      setSubiendo(false);
    }
  };

  const handleEliminar = async (evidenciaId) => {
    const confirmed = await confirm({
      title: 'Eliminar evidencia',
      message: '¿Estás seguro de eliminar esta evidencia?',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarEvidencia(id: $id) { success message } }`, { id: evidenciaId });
      const data = gqlR.eliminarEvidencia;

      if (data.success) {
        success('Evidencia eliminada');
        cargarEvidencias();
      } else {
        error('Error: ' + data.message);
      }
    } catch (err) {
      console.error('Error al eliminar evidencia:', err);
      error('Error al eliminar la evidencia');
    }
  };

  const handleVerEvidencia = (evidencia) => {
    const url = `${UPLOAD_URL}/catalogo/img/evidencias/${evidencia.ruta_archivo}`;
    window.open(url, '_blank');
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-evidencias" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📸 Evidencias Visuales</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-subheader">
          <p>{actividad?.descripcion}</p>
        </div>

        {/* Formulario de Subida */}
        <form onSubmit={handleSubir} className="evidencia-upload-form">
          <div className="upload-section">
            <div className="file-input-wrapper">
              <input
                type="file"
                id="file-input"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="file-input" className="file-input-label">
                📁 Seleccionar Foto o Documento
              </label>
              {formData.archivo && (
                <span className="file-name">{formData.archivo.name}</span>
              )}
            </div>

            {vistaPrevia && (
              <div className="vista-previa">
                {formData.archivo.type.startsWith('image/') ? (
                  <img src={vistaPrevia} alt="Vista previa" />
                ) : formData.archivo.type === 'application/pdf' ? (
                  <div className="pdf-placeholder">
                    <span>📄</span>
                    <p>Documento PDF</p>
                  </div>
                ) : (
                  <div className="file-placeholder">
                    <span>📎</span>
                    <p>Archivo seleccionado</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <input
                type="text"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Describe brevemente esta evidencia..."
              />
            </div>

            <div className="form-group">
              <label>Fecha de Captura</label>
              <input
                type="date"
                name="fecha_captura"
                value={formData.fecha_captura}
                onChange={handleChange}
              />
            </div>
          </div>

          <button type="submit" className="btn-subir" disabled={subiendo || !formData.archivo}>
            {subiendo ? 'Subiendo...' : '⬆️ Subir Evidencia'}
          </button>
        </form>

        {/* Lista de Evidencias */}
        <div className="evidencias-lista">
          <h3>📚 Evidencias Registradas ({evidencias.length})</h3>

          {loading ? (
            <div className="loading-state">
              <span>⏳</span>
              <p>Cargando evidencias...</p>
            </div>
          ) : evidencias.length === 0 ? (
            <div className="empty-state">
              <span>📷</span>
              <p>No hay evidencias registradas</p>
              <small>Sube la primera foto o documento de esta actividad</small>
            </div>
          ) : (
            <div className="evidencias-grid">
              {evidencias.map((evidencia) => (
                <div key={evidencia.id} className="evidencia-card">
                  <div className="evidencia-preview">
                    {evidencia.tipo === 'foto' ? (
                      <img
                        src={`${UPLOAD_URL}/catalogo/img/evidencias/${evidencia.ruta_archivo}`}
                        alt={evidencia.descripcion || 'Evidencia'}
                        onClick={() => handleVerEvidencia(evidencia)}
                      />
                    ) : (
                      <div className="documento-preview" onClick={() => handleVerEvidencia(evidencia)}>
                        <span className="documento-icon">📄</span>
                        <span className="ver-icon">👁️</span>
                      </div>
                    )}
                    <span className="evidencia-tipo-badge">
                      {evidencia.tipo === 'foto' ? '📷 Foto' : '📄 Documento'}
                    </span>
                  </div>

                  <div className="evidencia-info">
                    {evidencia.descripcion && (
                      <p className="evidencia-descripcion">{evidencia.descripcion}</p>
                    )}
                    <span className="evidencia-fecha">
                      📅 {new Date(evidencia.fecha_captura).toLocaleDateString('es-CO')}
                    </span>
                  </div>

                  <div className="evidencia-actions">
                    <button
                      className="btn-ver"
                      onClick={() => handleVerEvidencia(evidencia)}
                      title="Ver evidencia"
                    >
                      👁️ Ver
                    </button>
                    <button
                      className="btn-eliminar-evidencia"
                      onClick={() => handleEliminar(evidencia.id)}
                      title="Eliminar evidencia"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalEvidencias;
